// frontend/src/components/agent/AgentDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Mail } from 'lucide-react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';
import MessagesView from '../shared/MessagesView';
import RulesView from '../shared/RulesView';
import PerformanceTab from '../PerformanceTab';
import CustomerAdminSection from './CustomerAdminSection';
import SubAgentAdminSection from './SubAgentAdminSection';
import CreateAccountSection from './CreateAccountSection';
import GameAdminSection from './GameAdminSection';
import FeedbackSection from '../book/FeedbackSection';
import FaqSection from '../book/FaqSection';

export default function AgentDashboard({ user, onLogout }) {
  const [perms, setPerms] = useState(null);
  const [stats, setStats] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [showMessages, setShowMessages] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const checkUnread = async () => {
    try {
      const data = await api.getUnreadCount();
      setUnreadCount(data.unread_count);
    } catch (err) {
      console.error('Failed to get unread count:', err);
    }
  };

  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [permsData, dashData] = await Promise.all([
          api.getAgentPermissions(user.id),
          api.getDashboard(),
        ]);
        setPerms(permsData);
        setStats(dashData.stats);
      } catch (err) {
        setLoadError(err.response?.data?.error || 'Failed to load dashboard');
      }
    })();
    checkUnread();
  }, [user.id]);

  // Signed currency: "-$2,500.00" when down, "$1,250.00" when up.
  const money = (n) => `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const balanceTile = (label, value, color) => (
    <div style={{ background: colors.cardBg, boxShadow: colors.shadowCard, borderRadius: 8, padding: '1rem' }}>
      <p style={{ color: colors.textMuted, fontSize: '0.8rem', marginBottom: 4 }}>{label}</p>
      <p style={{ color, fontSize: '1.4rem', fontWeight: 700 }}>{value}</p>
    </div>
  );

  if (loadError) {
    return (
      <div style={{ minHeight: '100vh', background: colors.bgGradient, fontFamily, padding: '1rem' }}>
        <p style={{ color: colors.negative }}>{loadError}</p>
        <button onClick={onLogout} style={{ marginTop: '1rem', background: colors.cardBg, border: 'none', borderRadius: 8, padding: '0.6rem 1rem', cursor: 'pointer', color: colors.textPrimary, fontFamily }}>
          Log Out
        </button>
      </div>
    );
  }

  if (!perms || !stats) {
    return (
      <div style={{ minHeight: '100vh', background: colors.bgGradient, fontFamily, padding: '1rem' }}>
        <p style={{ color: colors.textMuted }}>Loading...</p>
      </div>
    );
  }

  const SECTIONS = [
    { key: 'weekly_figures', label: 'Weekly Figures', show: true },
    { key: 'customer_admin', label: 'Customer Admin', show: true },
    { key: 'agent_admin', label: 'Agent Admin', show: !!perms.can_add_agents },
    { key: 'create_account', label: 'Create Account', show: !!perms.can_add_customers || !!perms.can_add_agents },
    { key: 'game_admin', label: 'Game Admin', show: !!perms.can_access_game_admin },
    { key: 'feedback', label: 'Feedback', show: !!perms.can_access_feedback },
    { key: 'rules', label: 'Rules', show: true },
    { key: 'faq', label: 'F.A.Q', show: true },
  ].filter(s => s.show);

  const renderSection = () => {
    switch (activeSection) {
      case 'weekly_figures': return <PerformanceTab user={user} canViewIp={!!perms.can_view_ip} />;
      case 'customer_admin': return <CustomerAdminSection perms={perms} />;
      case 'agent_admin': return <SubAgentAdminSection currentUser={user} perms={perms} />;
      case 'create_account': return <CreateAccountSection perms={perms} />;
      case 'game_admin': return <GameAdminSection />;
      case 'feedback': return <FeedbackSection />;
      case 'rules': return <RulesView />;
      case 'faq': return <FaqSection />;
      default: return null;
    }
  };

  if (showMessages) {
    return (
      <div style={{ minHeight: '100vh', background: colors.bgGradient, fontFamily, padding: '1rem' }}>
        <button onClick={() => { setShowMessages(false); checkUnread(); }} style={{ marginBottom: '1rem', background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 6, padding: '0.5rem 1rem', cursor: 'pointer', fontFamily }}>
          ← Back
        </button>
        <MessagesView user={user} onUpdateUnread={checkUnread} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: colors.bgGradient, fontFamily, padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h1 style={{ color: colors.textPrimary, margin: 0 }}>{user.name}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button onClick={() => setShowMessages(true)} style={{ position: 'relative', background: colors.cardBg, border: 'none', borderRadius: 8, padding: '0.6rem', cursor: 'pointer', color: colors.textPrimary }}>
            <Mail size={20} />
            {unreadCount > 0 && (
              <span style={{ position: 'absolute', top: -4, right: -4, background: colors.negative, color: '#fff', fontSize: '0.65rem', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {unreadCount}
              </span>
            )}
          </button>
          <button onClick={onLogout} style={{ background: colors.cardBg, border: 'none', borderRadius: 8, padding: '0.6rem 1rem', cursor: 'pointer', color: colors.textPrimary, fontFamily }}>
            Log Out
          </button>
        </div>
      </div>

      {activeSection ? (
        <div>
          <button onClick={() => setActiveSection(null)} style={{ marginBottom: '1rem', background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 6, padding: '0.5rem 1rem', cursor: 'pointer', fontFamily }}>
            ← Back to Dashboard
          </button>
          {renderSection()}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {balanceTile('My Earnings', `$${(stats.agent_earnings ?? 0).toFixed(2)}`, (stats.agent_earnings ?? 0) >= 0 ? colors.odds : colors.negative)}
            {balanceTile('Weekly Customer Balance', money(stats.weekly_customer_balance ?? 0), (stats.weekly_customer_balance ?? 0) >= 0 ? colors.odds : colors.negative)}
            {balanceTile('Today Customer Balance', money(stats.today_customer_balance ?? 0), (stats.today_customer_balance ?? 0) >= 0 ? colors.odds : colors.negative)}
            {balanceTile('My Customers', stats.total_customers ?? 0, colors.textPrimary)}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem' }}>
            {SECTIONS.map(s => (
              <button
                key={s.key}
                onClick={() => setActiveSection(s.key)}
                style={{ padding: '1.25rem 1rem', borderRadius: 10, border: 'none', cursor: 'pointer', background: colors.cardBg, boxShadow: colors.shadowCard, color: colors.textPrimary, fontFamily, fontSize: '0.95rem', fontWeight: 600, textAlign: 'center' }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
